import { Navigate, useLocation } from "react-router-dom";
import { useAppSelector } from "@/stores/hook";
import useRouterData from "@/hooks/api/useRouterData";
import FullScreenLoader from "@/components/feedback/full-screen-loader/FullScreenLoader";
import NotFoundPage from "@/components/feedback/error/NotFoundPage";
import { RedirectToLogin } from "@/features/auth/routes/loginReturn";
import { resolveHomePath, isServerGeneratedDynamicPath } from "./homePath";

/** Tabs are absent OR a refetch is in flight — the route table isn't decided yet. */
function useRouteTableSettling() {
  const { tabs, isLoading, isFetching } = useRouterData();
  return { tabs, settling: isLoading || isFetching || !tabs };
}

/**
 * The `/` route for roles WITHOUT the dashboard tab. `buildDynamicRoutes` only
 * registers `/` when `serverData.dashboard` exists, so without this the
 * post-login `navigate("/")` and every "Go Back Home" link 404 for those users.
 */
export function HomeRedirect() {
  const { tabs, settling } = useRouteTableSettling();

  if (settling) return <FullScreenLoader />;

  const home = resolveHomePath(tabs);
  // `home !== "/"` guards the redirect loop: if home somehow resolves back to
  // `/` we render a terminal state instead of bouncing forever.
  if (home && home !== "/") return <Navigate to={home} replace />;

  return <NoAccessiblePage />;
}

/**
 * Catch-all INSIDE the app shell (`AppLayout`). Replaces the bare
 * `<NotFoundPage />` so a tenant/user switch can't dead-end the session.
 */
export function AppNotFound() {
  const { tabs, settling } = useRouteTableSettling();
  const { pathname } = useLocation();

  // The route table is still being rebuilt — right after a login, or after a
  // stage/view mutation invalidated ["router-tabs"]. Judging the URL now would
  // flash a 404 for a path that is about to exist.
  if (settling) return <FullScreenLoader />;

  const home = resolveHomePath(tabs);

  // A stale server-minted link from another tenant/user/role. Send them to
  // their own home rather than a dead end.
  if (home && isServerGeneratedDynamicPath(pathname)) {
    return <Navigate to={home} replace />;
  }

  return <NotFoundPage homePath={home ?? "/"} />;
}

/**
 * Top-level catch-all, used while `tabs` hasn't loaded (the protected route
 * tree isn't registered yet, so every path lands here).
 */
export function RootCatchAll() {
  const { tabs, isLoading, isFetching, error } = useRouterData();
  const storeToken = useAppSelector((state) => state.auth.token);
  const hasToken =
    !!storeToken ||
    (typeof window !== "undefined" &&
      !!localStorage.getItem("xale_access_token"));

  if (!hasToken) return <RedirectToLogin />;

  // A failed tabs query is NOT proof the session is dead while a refetch is
  // already in flight — that is the normal state for a second or two after a
  // login swaps the token. Bouncing to /login here used to throw a
  // just-signed-in user straight back to the sign-in form.
  if (isLoading || isFetching) return <FullScreenLoader />;
  if (error) return <RedirectToLogin />;
  if (!tabs) return <FullScreenLoader />;

  // Reaching this catch-all WITH tabs loaded means the current router was
  // built before they arrived. `/` is registered in EVERY route table
  // (dashboard or HomeRedirect), so a 404 at `/` is never a bad URL — it is
  // always a router/tabs desync (e.g. the tabs observer detached by a
  // login's cache teardown). One hard reload rebuilds everything from the
  // fresh session; the timestamp guard stops a reload loop if the desync
  // somehow survives the boot.
  if (window.location.pathname === "/" && shouldSelfHealReload()) {
    window.location.reload();
    return <FullScreenLoader />;
  }

  return <NotFoundPage homePath={resolveHomePath(tabs) ?? "/"} />;
}

const SELF_HEAL_KEY = "xale_root_404_reloaded_at";

function shouldSelfHealReload(): boolean {
  try {
    const last = Number(sessionStorage.getItem(SELF_HEAL_KEY) ?? 0);
    if (Date.now() - last < 15_000) return false;
    sessionStorage.setItem(SELF_HEAL_KEY, String(Date.now()));
    return true;
  } catch {
    // Storage unavailable — can't guard against a loop, so don't auto-reload.
    return false;
  }
}

/** Signed in, but the role carries no reachable tab at all. */
function NoAccessiblePage() {
  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen p-4"
      style={{ backgroundColor: "var(--color-background)" }}
    >
      <div className="text-center max-w-lg">
        <h2
          className="text-3xl font-bold mb-3 tracking-tight"
          style={{ color: "var(--color-black-9)" }}
        >
          No pages available
        </h2>
        <p className="text-lg" style={{ color: "var(--color-black-6)" }}>
          Your role doesn't have access to any page yet. Ask an admin to grant
          you access, then reload.
        </p>
      </div>
    </div>
  );
}
